import {
  capitalizeFirstLetter,
  careEnvironmentDefs,
  carePhaseDefs,
  formatCurrency,
  formatPercent,
  CareEnvironment,
  CarePhase,
  pickClientCareEnvironmentCosts,
  isHealthcareInflationRateDefault,
} from '@shared';
import { analytics } from '@shared/posthog';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import CareEnvironmentCostModal from './CareEnvironmentCostModal';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  previewClientUpdates,
  putClientCustomInferencesForClientByClientIdRequest,
} from '@client/model/clientModel';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { EditIconButton } from './EditIconButton';
import EditHealthCareInflationModal from './EditHealthCareInflationModal';
import { Switch, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { Info } from 'lucide-react';
import { PhaseInflationScheduleTooltip } from './PhaseInflationScheduleTooltip';
import { appliedHealthCareInflation } from '@shared';
import { usePhaseInflationSchedule } from '@client/hooks/usePhaseInflationSchedule';

interface CarePhaseCostSummaryProps {
  client: Client;
  carePhase: CarePhase;
}

export function CarePhaseCostSummary({ client, carePhase }: CarePhaseCostSummaryProps) {
  const {
    intakeSurvey: { clientLtcZipCode, clientCurrentCarePhase },
    phaseCosts,
    clientYearsTillPhaseEnd,
    clientPhaseInflationFactors,
    clientCalculationSettings: { healthCareInflationRate },
  } = client;

  const dispatch = useDispatch();
  const { flags } = useFeatureFlags();
  const [openEditCostModal, setOpenEditCostModal] = useState(false);
  const [openEditInflationModal, setOpenEditInflationModal] = useState(false);
  // With the claim-projection engine on, the factor is the month-weighted
  // AVERAGE of the phase's annually-stepped growth, so the sublabel drops the
  // year count and an info tooltip presents the per-month schedule instead.
  const { useMonthBasedGrowth, inflationSchedule } = usePhaseInflationSchedule(client, carePhase);

  const isFullCarePhase = carePhase === CarePhase.fullCare;
  const showFullCare247Toggle = flags.fullCare247Override && isFullCarePhase;
  const carePhaseIndex = carePhaseDefs[carePhase].index;
  const selectedCarePhaseCosts = phaseCosts[carePhaseIndex];

  // phase is in the past
  if (clientCurrentCarePhase && carePhaseIndex < carePhaseDefs[clientCurrentCarePhase].index) {
    return null;
  }

  const yearsTillPhaseEnd = clientYearsTillPhaseEnd[carePhase];
  const phaseInflationFactor = clientPhaseInflationFactors[carePhase];
  const selectedCareSetting = client.careEnvironmentSelections?.[carePhase];
  const isCareSettingHomeCare = selectedCareSetting === CareEnvironment.home;
  const clientCareEnvironmentCostData = selectedCareSetting
    ? pickClientCareEnvironmentCosts(client, selectedCareSetting)
    : undefined;

  const isDurationCustom = selectedCarePhaseCosts.isDurationCustom;
  const rateLocalDescription = clientCareEnvironmentCostData?.rateLocaleDescription;

  // Independent Living (claim-projection engine): the phase splits into
  // home-style care (policy-eligible) priced at the home hourly rate + room &
  // board (out of pocket) priced at the IL monthly rate. `phaseRoomAndBoardCost`
  // is 0 for every other environment / on the legacy engine, so this display
  // path only activates for an IL phase under the new engine.
  const hasRoomAndBoard = (selectedCarePhaseCosts.phaseRoomAndBoardCost ?? 0) > 0;
  const homeCareCostData = hasRoomAndBoard
    ? pickClientCareEnvironmentCosts(client, CareEnvironment.home)
    : undefined;
  const careRateLabel = `${Math.round(
    selectedCarePhaseCosts.phaseProfessionalCareHoursProvided,
  ).toLocaleString()} hrs${
    homeCareCostData ? ` × ${formatCurrency(homeCareCostData.rateAmount)}/hr` : ''
  }`;
  const roomBoardRateLabel = `${Math.round(selectedCarePhaseCosts.phaseCareMonthsNeeded)} mo${
    clientCareEnvironmentCostData
      ? ` × ${formatCurrency(clientCareEnvironmentCostData.rateAmount)}/mo`
      : ''
  }`;
  const phaseInflatedTotal = hasRoomAndBoard
    ? selectedCarePhaseCosts.phaseInflatedProfessionalShareCost +
      selectedCarePhaseCosts.phaseInflatedRoomAndBoardCost
    : selectedCarePhaseCosts.phaseInflatedProfessionalShareCost;

  return (
    <div className="border-primary-200 bg-primary-100 rounded-lg border-[1.5px] border-dashed px-4 py-6 lg:flex-grow lg:px-6">
      <h2
        id="summary-heading"
        className="text-base font-medium text-gray-800 md:text-lg"
      >
        Cost Summary ({carePhaseDefs[carePhase].label})
      </h2>

      <dl className="mt-4 space-y-3 md:mt-6">
        {hasRoomAndBoard ? (
          <>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-800 md:text-base">
                <span className="inline-flex items-center gap-1">
                  Care services
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground size-3.5 shrink-0 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      Hands-on caregiving in independent living, priced at the local home-care
                      hourly rate. This portion is eligible for policy coverage.
                    </TooltipContent>
                  </Tooltip>
                </span>
                <div className="text-sm text-gray-700">{careRateLabel} · covered</div>
              </dt>
              <dd className="text-end text-sm font-medium text-gray-950 md:text-base">
                <LtcCostDisplay value={selectedCarePhaseCosts.phaseProfessionalShareCost} />
              </dd>
            </div>
            <div className="border-primary-200 flex items-center justify-between border-t pt-4">
              <dt className="text-sm text-gray-800 md:text-base">
                <span className="inline-flex items-center gap-1">
                  Room &amp; board
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground size-3.5 shrink-0 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      The base cost of independent living — housing, meals, and amenities.
                      Long-term care policies generally don&apos;t cover this, so it&apos;s paid out
                      of pocket.
                    </TooltipContent>
                  </Tooltip>
                </span>
                <div className="text-sm text-gray-700">{roomBoardRateLabel} · out of pocket</div>
              </dt>
              <dd className="flex items-center text-end text-sm font-medium text-gray-950 md:text-base">
                <EditIconButton
                  onClick={() => setOpenEditCostModal(true)}
                  tooltipText="Edit room & board cost"
                />
                <LtcCostDisplay value={selectedCarePhaseCosts.phaseRoomAndBoardCost} />
              </dd>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-gray-800 md:text-base">
                {selectedCareSetting
                  ? isCareSettingHomeCare
                    ? 'Home care'
                    : capitalizeFirstLetter(careEnvironmentDefs[selectedCareSetting].label)
                  : 'Care setting'}{' '}
                cost
                <div className="text-sm text-gray-700">{rateLocalDescription}</div>
              </dt>
              <dd
                className={`${selectedCareSetting ? '' : 'hidden'} flex items-center text-sm font-medium text-gray-800 md:text-base`}
              >
                <EditIconButton
                  onClick={() => setOpenEditCostModal(true)}
                  tooltipText="Edit cost"
                />

                {formatCurrency(clientCareEnvironmentCostData?.rateAmount) +
                  '/' +
                  (isCareSettingHomeCare ? 'hr' : 'mo')}
              </dd>
              <dd
                className={`${selectedCareSetting ? 'hidden' : ''} flex items-center text-base font-medium text-gray-800`}
              >
                $ --
              </dd>
            </div>
            <div className="border-primary-200 border-t pt-4">
              <div className="flex items-center justify-between">
                <dt className="text-sm text-gray-800 md:text-base">
                  <span>
                    {isCareSettingHomeCare
                      ? 'Professional care hours'
                      : isDurationCustom
                        ? 'Custom duration'
                        : 'Estimated duration'}
                  </span>
                </dt>
                <dd className="text-end text-sm font-medium text-gray-950 md:text-base">
                  {isCareSettingHomeCare
                    ? Math.round(
                        selectedCarePhaseCosts.phaseProfessionalCareHoursProvided,
                      ).toLocaleString() + ' hrs'
                    : Math.round(selectedCarePhaseCosts.phaseCareMonthsNeeded) + ' months'}
                </dd>
              </div>
              {showFullCare247Toggle && isCareSettingHomeCare && (
            <div className="mt-3 flex items-center gap-2">
              <Switch
                id="full-care-247-toggle"
                checked={client.clientCustomInferences.fullCare247Override ?? false}
                onCheckedChange={(checked: boolean) => {
                  const previousHours = selectedCarePhaseCosts.phaseProfessionalCareHoursProvided;
                  const updatedInferences = {
                    ...client.clientCustomInferences,
                    fullCare247Override: checked,
                  };
                  dispatch(
                    previewClientUpdates({ ...client, clientCustomInferences: updatedInferences }),
                  );
                  dispatch(
                    putClientCustomInferencesForClientByClientIdRequest({
                      clientId: client.clientId,
                      clientCustomInferences: { fullCare247Override: checked },
                    }),
                  );

                  // Track toggle change
                  analytics.carePlanning.toggle247Coverage({
                    clientId: client.clientId,
                    enabled: checked,
                    carePhase: 'fullCare',
                    careEnvironment: 'home',
                    previousHours: Math.round(previousHours),
                  });
                }}
              />
              <label
                htmlFor="full-care-247-toggle"
                className="text-muted-foreground cursor-pointer text-xs"
              >
                Apply 24/7 care availability
              </label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="text-muted-foreground size-3.5 shrink-0 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Full care at home often requires 24/7 support. This overrides the predicted active
                  care hours to reflect round-the-clock care needs.
                </TooltipContent>
              </Tooltip>
            </div>
          )}
            </div>
          </>
        )}
        <div className="border-primary-200 flex items-center justify-between border-t pt-4">
          <dt className="text-sm text-gray-800 md:text-base">
            Healthcare inflation
            <div className="flex items-center gap-1 text-sm text-gray-700">
              {useMonthBasedGrowth && phaseInflationFactor ? (
                <>
                  {`Avg. cost growth${isHealthcareInflationRateDefault(healthCareInflationRate) ? '' : ' (edited)'}`}
                  <PhaseInflationScheduleTooltip
                    inflationRate={appliedHealthCareInflation(client)}
                    schedule={inflationSchedule}
                    phaseInflationFactor={phaseInflationFactor}
                  />
                </>
              ) : (
                `Over ${
                  yearsTillPhaseEnd! < 1
                    ? `${Math.round(selectedCarePhaseCosts.phaseCareMonthsNeeded)} months`
                    : `${yearsTillPhaseEnd!.toFixed(1)} year${yearsTillPhaseEnd === 1 ? '' : 's'}`
                }${!isHealthcareInflationRateDefault(healthCareInflationRate) ? ' (edited)' : ''}`
              )}
            </div>
          </dt>

          <dd className="text-sm font-medium text-gray-800 md:text-base">
            <EditIconButton
              onClick={() => setOpenEditInflationModal(true)}
              tooltipText="Edit inflation rate"
            />
            {formatPercent(phaseInflationFactor, 1)}
          </dd>
        </div>
        <div className="border-primary-200 flex items-center justify-between border-t pt-4">
          <dt className="text-sm font-medium text-gray-800 md:text-base">Total</dt>
          <dd className="text-sm font-semibold text-gray-800 md:text-base">
            {selectedCareSetting ? <LtcCostDisplay value={phaseInflatedTotal} /> : '$ --'}
          </dd>
        </div>
      </dl>
      {selectedCareSetting && (
        <CareEnvironmentCostModal
          selectedCareEnvironment={selectedCareSetting}
          open={openEditCostModal}
          setOpen={setOpenEditCostModal}
        />
      )}
      <EditHealthCareInflationModal
        open={openEditInflationModal}
        setOpen={setOpenEditInflationModal}
      />
    </div>
  );
}
